import { LookupCombobox } from '../../components/ui/lookup-combobox';
import { CenteredDialog } from '../../components/ui/centered-dialog';
// apps/web/src/pages/pcp/omie-clientes-page.tsx — Fase 04, Planos 01, 02 e 03 (OMIE-01, OMIE-06, D-01..D-04).
// Fase 09, Plano 06 (D-01, D-04): o formulário "Criar cliente e enviar para a Omie" foi
// removido deliberadamente — a única entrada de criação de cliente é a página dedicada de
// Cadastros (/cadastros/clientes/novo). Esta página permanece estritamente operacional:
// sincronização de catálogo, importação em lote, vínculo/conciliação e acompanhamento de
// status, todos preservados sem duplicar a superfície de criação.
import React, { useEffect, useState, useMemo } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  RefreshCw,
  UserCheck,
  Search,
  ShieldCheck,
  PlusCircle,
  X,
} from 'lucide-react';
import type {
  OmieOperacaoView,
  PreviaClienteOmieItem,
  PreviaClientesOmieResponse,
} from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import { TablePagination } from '../../components/ui/table-pagination';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

function mensagemDaResposta(valor: unknown, padrao: string): string {
  if (typeof valor !== 'object' || valor === null) return padrao;
  const mensagem = (valor as Record<string, unknown>).message;
  return typeof mensagem === 'string' ? mensagem : padrao;
}

function registro(valor: unknown): Record<string, unknown> {
  return typeof valor === 'object' && valor !== null ? valor as Record<string, unknown> : {};
}

function codigoConcluido(operacao: OmieOperacaoView): string | null {
  const dados = registro(operacao);
  if (typeof dados.codigoOmie === 'string') return dados.codigoOmie;
  const codigo = registro(dados.resultado).codigo_cliente_omie;
  return typeof codigo === 'string' || typeof codigo === 'number' ? String(codigo) : null;
}

export const OmieClientesPage: React.FC = () => {
  // Estado da sincronização / importação direta
  const [erro, setErro] = useState<string | null>(null);
  const [operacao, setOperacao] = useState<OmieOperacaoView | null>(null);
  const [mensagemSucesso, setMensagemSucesso] = useState<string | null>(null);

  // Estado da prévia de conciliação
  const [previa, setPrevia] = useState<PreviaClientesOmieResponse | null>(null);
  const [loadingPrevia, setLoadingPrevia] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState<string>('TODAS');
  const [buscaTexto, setBuscaTexto] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(30);
  const [sincronizandoCatalogo, setSincronizandoCatalogo] = useState(false);
  const [importandoLote, setImportandoLote] = useState(false);
  const [ativandoLote, setAtivandoLote] = useState(false);

  // Modal de Confirmação de Vínculo
  const [itemParaVincular, setItemParaVincular] = useState<PreviaClienteOmieItem | null>(null);
  const [modalClienteId, setModalClienteId] = useState('');
  const [modalCriarNovo, setModalCriarNovo] = useState(false);
  const [modalConfirmado, setModalConfirmado] = useState(false);
  const [modalJustificativa, setModalJustificativa] = useState('');
  const [modalErro, setModalErro] = useState<string | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Carregar prévia de conciliação
  const carregarPrevia = async () => {
    setLoadingPrevia(true);
    setErro(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/omie/clientes/pre-importacao?pagina=${pagina}&limite=${limite}${
          filtroCategoria !== 'TODAS' ? `&categoria=${filtroCategoria}` : ''
        }`,
        { credentials: 'include' },
      );
      if (res.ok) {
        const data = (await res.json()) as PreviaClientesOmieResponse;
        setPrevia(data);
      } else {
        const errData: unknown = await res.json().catch(() => null);
        setErro(mensagemDaResposta(errData, 'Falha ao carregar prévia de importação da Omie.'));
      }
    } catch {
      setErro('Erro de conexão ao buscar prévia da Omie.');
    } finally {
      setLoadingPrevia(false);
    }
  };

  useEffect(() => {
    void carregarPrevia();
  }, [pagina, limite, filtroCategoria]);

  // Polling autenticado a cada 2s enquanto a operação estiver pendente
  useEffect(() => {
    if (!operacao || (operacao.status !== 'AGUARDANDO_FILA' && operacao.status !== 'PROCESSANDO')) {
      return;
    }

    const consultarStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/omie/clientes/importacoes/${operacao.id}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const updated = (await res.json()) as OmieOperacaoView;
          setOperacao(updated);
          if (updated.status === 'CONCLUIDA') {
            void carregarPrevia();
          }
        }
      } catch (erro) {
        console.error('Falha no polling de status da operação Omie', erro);
      }
    };
    const interval = setInterval(() => void consultarStatus(), 2000);

    return () => clearInterval(interval);
  }, [operacao]);

  // Disparar sincronização do catálogo completo em background
  const handleSincronizarCatalogo = async () => {
    setSincronizandoCatalogo(true);
    setErro(null);
    try {
      const res = await apiMutate('/api/v1/omie/clientes/sincronizar', {
        method: 'POST',
        body: {},
      });
      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha ao iniciar sincronização de clientes.'));
      }
      const op = (await res.json()) as OmieOperacaoView;
      setOperacao(op);
      setMensagemSucesso('Sincronização em segundo plano iniciada.');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao iniciar sincronização.');
    } finally {
      setSincronizandoCatalogo(false);
    }
  };

  // Importar em LOTE todos os clientes Omie ainda não vinculados como novos.
  const handleImportarTodos = async () => {
    const ok = window.confirm(
      'Importar TODOS os clientes da Omie ainda não vinculados como NOVOS clientes locais?\n\n' +
        'Use na primeira migração. É idempotente — clientes já vinculados são pulados.',
    );
    if (!ok) return;
    setImportandoLote(true);
    setErro(null);
    setMensagemSucesso(null);
    try {
      const res = await apiMutate('/api/v1/omie/clientes/importar-todos', {
        method: 'POST',
        body: { justificativa: 'Importação inicial em lote (primeira migração)' },
      });
      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha na importação em lote.'));
      }
      const r = (await res.json()) as { total: number; jaVinculados: number; criados: number; falhas: number };
      setMensagemSucesso(
        `Importação concluída: ${r.criados} criado(s), ${r.jaVinculados} já vinculado(s)` +
          (r.falhas > 0 ? `, ${r.falhas} falha(s)` : '') +
          ` de ${r.total}. Recarregue a prévia e veja em Cadastros → Clientes.`,
      );
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro na importação em lote.');
    } finally {
      setImportandoLote(false);
    }
  };

  // Ativar em lote os clientes vinculados, exceto os ambíguos (nome duplicado).
  const handleAtivarNaoAmbiguos = async () => {
    const ok = window.confirm(
      'Ativar TODOS os clientes vinculados, EXCETO os ambíguos (razão social duplicada)?\n\n' +
        'Os ambíguos ficam inativos para revisão manual.',
    );
    if (!ok) return;
    setAtivandoLote(true);
    setErro(null);
    setMensagemSucesso(null);
    try {
      const res = await apiMutate('/api/v1/omie/clientes/ativar-nao-ambiguos', {
        method: 'POST',
        body: { justificativa: 'Ativação em lote pós-migração (exceto ambíguos)' },
      });
      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha na ativação em lote.'));
      }
      const r = (await res.json()) as { total: number; ativados: number; ambiguosPulados: number };
      setMensagemSucesso(
        `Ativação concluída: ${r.ativados} ativado(s), ${r.ambiguosPulados} ambíguo(s) mantido(s) inativo(s) para revisão.`,
      );
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro na ativação em lote.');
    } finally {
      setAtivandoLote(false);
    }
  };

  // Abrir diálogo de vínculo para um item
  const abrirDialogoVinculo = (item: PreviaClienteOmieItem) => {
    setItemParaVincular(item);
    setModalClienteId(item.sugestaoClienteId || item.clienteIdVinculado || '');
    setModalCriarNovo(item.categoria === 'SEM_CORRESPONDENCIA' && !item.sugestaoClienteId);
    setModalConfirmado(false);
    setModalJustificativa(
      item.categoria === 'EXATA_CODIGO' || item.categoria === 'EXATA_NOME'
        ? 'Correspondência exata validada pelo PCP'
        : '',
    );
    setModalErro(null);
  };

  // Confirmar vínculo permanente
  const handleConfirmarVinculoModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemParaVincular) return;

    if (!modalConfirmado) {
      setModalErro('A confirmação expressa do vínculo permanente é obrigatória (D-01).');
      return;
    }

    if (!modalJustificativa.trim()) {
      setModalErro('A justificativa da conciliação é obrigatória (D-01).');
      return;
    }

    setModalLoading(true);
    setModalErro(null);

    try {
      const res = await apiMutate(
        `/api/v1/omie/clientes/${itemParaVincular.codigoClienteOmie}/confirmar-vinculo`,
        {
          method: 'POST',
          body: {
            clienteId: modalCriarNovo ? undefined : modalClienteId.trim() || undefined,
            criarNovo: modalCriarNovo,
            confirmado: true,
            justificativa: modalJustificativa.trim(),
          },
        },
      );

      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha ao confirmar vínculo permanente.'));
      }

      setMensagemSucesso(
        `Cliente Omie ${itemParaVincular.codigoClienteOmie} vinculado com sucesso (D-01).`,
      );
      setItemParaVincular(null);
      void carregarPrevia();
    } catch (err) {
      setModalErro(err instanceof Error ? err.message : 'Erro ao confirmar vínculo.');
    } finally {
      setModalLoading(false);
    }
  };

  // Importação direta por formulário

  // Itens filtrados no cliente para busca rápida
  const itensFiltrados = useMemo(() => {
    if (!previa?.itens) return [];
    if (!buscaTexto.trim()) return previa.itens;
    const term = buscaTexto.toLowerCase();
    return previa.itens.filter(
      (item) =>
        item.razaoSocial.toLowerCase().includes(term) ||
        (item.nomeFantasia && item.nomeFantasia.toLowerCase().includes(term)) ||
        (item.codigoClienteIntegracao && item.codigoClienteIntegracao.toLowerCase().includes(term)) ||
        (item.cidade && item.cidade.toLowerCase().includes(term)) ||
        (item.telefonePrincipal && item.telefonePrincipal.includes(term)) ||
        String(item.codigoClienteOmie).includes(term),
    );
  }, [previa, buscaTexto]);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto text-slate-900">
      {/* Região de anúncio acessível para leitores de tela */}
      <div aria-live="polite" className="sr-only">
        {mensagemSucesso} {erro}
      </div>

      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <ExternalLink className="w-6 h-6 text-slate-800" />
            <h1 className="text-xl font-bold">Integração Omie — Gestão de Clientes</h1>
          </div>
          <p className="text-xs text-slate-500">
            Conciliação de catálogo, prévia em 5 categorias e vínculo auditado permanente (Fluxo 1 / D-01–D-04).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* D-01/D-04 (Fase 09, Plano 06): a criação de cliente vive exclusivamente em
              Cadastros; esta página aponta para lá em vez de duplicar um formulário. */}
          <a
            href="/cadastros/clientes/novo"
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 border border-slate-900 rounded-lg text-xs font-semibold text-white hover:bg-slate-800 transition-colors shadow-2xs"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Cadastrar novo cliente</span>
          </a>
          <button
            type="button"
            onClick={() => void handleSincronizarCatalogo()}
            disabled={sincronizandoCatalogo}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors shadow-2xs"
          >
            <RefreshCw className={`w-4 h-4 ${sincronizandoCatalogo ? 'animate-spin' : ''}`} />
            <span>Sincronizar Catálogo Completo</span>
          </button>
          <button
            type="button"
            onClick={() => void handleImportarTodos()}
            disabled={importandoLote || sincronizandoCatalogo}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 border border-blue-600 rounded-lg text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-2xs"
            title="Cria um cliente local para cada cliente Omie ainda não vinculado (primeira migração)."
          >
            <span>{importandoLote ? 'Importando…' : 'Importar todos (novos)'}</span>
          </button>
          <button
            type="button"
            onClick={() => void handleAtivarNaoAmbiguos()}
            disabled={ativandoLote || importandoLote}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 border border-emerald-600 rounded-lg text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-2xs"
            title="Ativa todos os clientes vinculados, exceto os ambíguos (razão social duplicada)."
          >
            <span>{ativandoLote ? 'Ativando…' : 'Ativar (exceto ambíguos)'}</span>
          </button>
        </div>
      </div>

      {/* Mensagens de Sucesso e Erro */}
      {mensagemSucesso && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{mensagemSucesso}</span>
          </div>
          <button
            type="button"
            onClick={() => setMensagemSucesso(null)}
            className="text-emerald-700 hover:text-emerald-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {erro && (
        <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{erro}</span>
          </div>
          <button
            type="button"
            onClick={() => setErro(null)}
            className="text-rose-700 hover:text-rose-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Painel de Operação Assíncrona Ativa */}
      {operacao && (
        <div className="bg-white border rounded-xl p-4 shadow-xs space-y-3">
          <div className="flex items-center justify-between border-b pb-2">
            <div className="space-y-0.5">
              <h3 className="text-xs font-bold text-slate-800">Status da Operação Assíncrona</h3>
              <p className="text-[11px] font-mono text-slate-400">ID: {operacao.id}</p>
            </div>

            {operacao.status === 'AGUARDANDO_FILA' && (
              <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-semibold">
                <Clock className="w-3.5 h-3.5" />
                <span>Aguardando Fila</span>
              </span>
            )}
            {operacao.status === 'PROCESSANDO' && (
              <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-xs font-semibold">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Processando na Omie</span>
              </span>
            )}
            {operacao.status === 'CONCLUIDA' && (
              <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full text-xs font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>
                  {codigoConcluido(operacao)
                    ? `Concluída — cliente criado com código Omie #${codigoConcluido(operacao)}`
                    : 'Concluída — sincronização realizada com sucesso.'}
                </span>
              </span>
            )}
            {operacao.status === 'ACAO_NECESSARIA' && (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-rose-50 text-rose-800 border border-rose-200 rounded-full text-xs font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>Ação Necessária ({operacao.diagnosticoCodigo || 'ERRO'})</span>
                </span>
                <button
                  type="button"
                  onClick={() => setOperacao(null)}
                  className="px-2 py-0.5 border border-rose-300 rounded text-xs font-semibold text-rose-800 hover:bg-rose-50"
                >
                  Tentar novamente
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resumo de Conciliação em Cards de Categorias */}
      {previa && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
          <button
            type="button"
            onClick={() => setFiltroCategoria('TODAS')}
            className={`p-3 rounded-lg border text-left transition-colors ${
              filtroCategoria === 'TODAS'
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="font-semibold text-[11px]">Total Registros</div>
            <div className="text-lg font-bold">{previa.total}</div>
          </button>

          <button
            type="button"
            onClick={() => setFiltroCategoria('AMBIGUA')}
            className={`p-3 rounded-lg border text-left transition-colors ${
              filtroCategoria === 'AMBIGUA'
                ? 'bg-amber-800 text-white border-amber-800'
                : 'bg-amber-50/50 text-amber-900 border-amber-200 hover:bg-amber-100/50'
            }`}
          >
            <div className="font-semibold text-[11px] flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <span>Ambíguas</span>
            </div>
            <div className="text-lg font-bold">{previa.resumo.ambigua}</div>
          </button>

          <button
            type="button"
            onClick={() => setFiltroCategoria('SEM_CORRESPONDENCIA')}
            className={`p-3 rounded-lg border text-left transition-colors ${
              filtroCategoria === 'SEM_CORRESPONDENCIA'
                ? 'bg-rose-800 text-white border-rose-800'
                : 'bg-rose-50/50 text-rose-900 border-rose-200 hover:bg-rose-100/50'
            }`}
          >
            <div className="font-semibold text-[11px] flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 text-rose-600" />
              <span>Sem Match</span>
            </div>
            <div className="text-lg font-bold">{previa.resumo.semCorrespondencia}</div>
          </button>

          <button
            type="button"
            onClick={() => setFiltroCategoria('SUGERIDA')}
            className={`p-3 rounded-lg border text-left transition-colors ${
              filtroCategoria === 'SUGERIDA'
                ? 'bg-blue-800 text-white border-blue-800'
                : 'bg-blue-50/50 text-blue-900 border-blue-200 hover:bg-blue-100/50'
            }`}
          >
            <div className="font-semibold text-[11px]">Sugeridas (&gt;92%)</div>
            <div className="text-lg font-bold">{previa.resumo.sugerida}</div>
          </button>

          <button
            type="button"
            onClick={() => setFiltroCategoria('EXATA_NOME')}
            className={`p-3 rounded-lg border text-left transition-colors ${
              filtroCategoria === 'EXATA_NOME'
                ? 'bg-emerald-800 text-white border-emerald-800'
                : 'bg-emerald-50/50 text-emerald-900 border-emerald-200 hover:bg-emerald-100/50'
            }`}
          >
            <div className="font-semibold text-[11px]">Exata por Nome</div>
            <div className="text-lg font-bold">{previa.resumo.exataNome}</div>
          </button>

          <button
            type="button"
            onClick={() => setFiltroCategoria('EXATA_CODIGO')}
            className={`p-3 rounded-lg border text-left transition-colors ${
              filtroCategoria === 'EXATA_CODIGO'
                ? 'bg-teal-800 text-white border-teal-800'
                : 'bg-teal-50/50 text-teal-900 border-teal-200 hover:bg-teal-100/50'
            }`}
          >
            <div className="font-semibold text-[11px]">Exata por Código</div>
            <div className="text-lg font-bold">{previa.resumo.exataCodigo}</div>
          </button>
        </div>
      )}

      {/* Barra de Filtros e Busca */}
      <div className="bg-white border rounded-xl p-4 shadow-xs flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrar por razão, fantasia ou código..."
            value={buscaTexto}
            onChange={(e) => setBuscaTexto(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 border rounded-lg text-xs bg-white text-slate-900 placeholder:text-slate-400 focus:ring-1 focus:ring-slate-900"
          />
        </div>

        <div className="text-xs text-slate-500 font-medium">
          Exibindo {itensFiltrados.length} cliente(s)
        </div>
      </div>

      {/* Lista / Tabela Tablet-Ready de Prévia de Clientes */}
      <div className="bg-white border rounded-xl shadow-xs overflow-hidden">
        {loadingPrevia ? (
          <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin text-slate-700" />
            <span className="text-xs">Carregando catálogo e calculando prévias...</span>
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="p-12 text-center text-xs text-slate-500">
            Nenhum registro encontrado para os filtros selecionados.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {itensFiltrados.map((item) => (
              <div
                key={item.codigoClienteOmie}
                className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 hover:bg-slate-50/75 transition-colors"
              >
                {/* Bloco 1: Dados Omie (Somente Leitura - D-02) */}
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-800 rounded">
                      Omie #{item.codigoClienteOmie}
                    </span>
                    {item.codigoClienteIntegracao && (
                      <span className="font-mono text-[11px] px-1.5 py-0.5 bg-slate-50 border text-slate-600 rounded">
                        Integ: {item.codigoClienteIntegracao}
                      </span>
                    )}
                    {item.inativo && (
                      <span className="text-[11px] px-2 py-0.5 bg-rose-100 text-rose-800 font-semibold rounded">
                        Inativo no ERP (D-03)
                      </span>
                    )}
                  </div>

                  <div className="font-bold text-sm text-slate-900 truncate">
                    {item.razaoSocial}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                    {item.nomeFantasia && (
                      <div className="truncate">
                        <span className="text-slate-400">Fantasia:</span> {item.nomeFantasia}
                      </div>
                    )}
                    <div>
                      <span className="text-slate-400">Doc:</span>{' '}
                      {item.documentoFiscalMascarado || 'Não informado'}
                    </div>
                    {item.telefonePrincipal && (
                      <div className="truncate">
                        <span className="text-slate-400">Telefone:</span> {item.telefonePrincipal}
                      </div>
                    )}
                    {(item.cidade || item.estado) && (
                      <div className="truncate">
                        <span className="text-slate-400">Cidade/UF:</span>{' '}
                        {[item.cidade, item.estado].filter(Boolean).join('/')}
                      </div>
                    )}
                    {item.enderecoResumido && (
                      <div className="sm:col-span-2 truncate text-[11px] text-slate-500">
                        <span className="text-slate-400">Endereço:</span> {item.enderecoResumido}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bloco 2: Correspondência Pieltec e Ação */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0">
                  <div className="space-y-1 text-right sm:text-left min-w-[180px]">
                    <div className="flex items-center gap-1.5">
                      {item.categoria === 'EXATA_CODIGO' && (
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-800 text-[11px] font-bold rounded">
                          Exata por Código
                        </span>
                      )}
                      {item.categoria === 'EXATA_NOME' && (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[11px] font-bold rounded">
                          Exata por Nome
                        </span>
                      )}
                      {item.categoria === 'SUGERIDA' && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[11px] font-bold rounded">
                          Sugerida ({Math.round((item.score ?? 0) * 100)}%)
                        </span>
                      )}
                      {item.categoria === 'AMBIGUA' && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-900 text-[11px] font-bold rounded flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-amber-700" />
                          <span>Ambiguidade</span>
                        </span>
                      )}
                      {item.categoria === 'SEM_CORRESPONDENCIA' && (
                        <span className="px-2 py-0.5 bg-rose-100 text-rose-800 text-[11px] font-bold rounded flex items-center gap-1">
                          <AlertCircle className="w-3 h-3 text-rose-700" />
                          <span>Sem Correspondência</span>
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-slate-700">
                      {item.clienteIdVinculado ? (
                        <span className="text-emerald-700 font-semibold flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Vinculado</span>
                        </span>
                      ) : item.sugestaoNome ? (
                        <span className="text-slate-600 truncate block max-w-[200px]">
                          Sugerido: <strong>{item.sugestaoNome}</strong>
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">Nenhum cliente local</span>
                      )}
                    </div>
                  </div>

                  {/* Ação de Conciliação */}
                  <button
                    type="button"
                    onClick={() => abrirDialogoVinculo(item)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors shadow-2xs"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    <span>{item.clienteIdVinculado ? 'Revisar Vínculo' : 'Conciliar / Vincular'}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {previa && previa.total > 0 && (
          <TablePagination
            page={pagina}
            pageSize={limite}
            totalItems={previa.total}
            itemLabel="clientes"
            onPageChange={setPagina}
            onPageSizeChange={(nextLimit) => {
              setLimite(nextLimit);
              setPagina(1);
            }}
          />
        )}
      </div>

      {/* Modal / Diálogo de Confirmação de Vínculo (D-01) */}
      {itemParaVincular && (
        <CenteredDialog title="Revisar vínculo com a Omie" onClose={() => setItemParaVincular(null)}>
          <div className="bg-white rounded-2xl border shadow-xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-100">
            <div className="flex items-center justify-between border-b pb-3">
              <div className="space-y-0.5">
                <h2 className="text-sm font-bold text-slate-900">
                  Revisar vínculo com a Omie
                </h2>
                <p className="text-[11px] text-slate-500 font-mono">
                  Código Omie: #{itemParaVincular.codigoClienteOmie}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setItemParaVincular(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Resumo Omie Somente Leitura */}
            <div className="p-3 bg-slate-50 border rounded-lg text-xs space-y-1 text-slate-700">
              <div className="font-bold text-slate-900">{itemParaVincular.razaoSocial}</div>
              <div className="text-slate-500 text-[11px]">
                Doc: {itemParaVincular.documentoFiscalMascarado || 'N/A'} | Integ:{' '}
                {itemParaVincular.codigoClienteIntegracao || 'N/A'}
              </div>
            </div>

            <form onSubmit={(evento) => void handleConfirmarVinculoModal(evento)} className="space-y-4">
              {/* Opção Criar Novo vs Vincular Existente */}
              <div className="space-y-2">
                <div className="flex items-center gap-4 text-xs font-semibold text-slate-700">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tipoVinculo"
                      checked={!modalCriarNovo}
                      onChange={() => setModalCriarNovo(false)}
                      className="text-slate-900 focus:ring-slate-900"
                    />
                    <span>Vincular a cliente local existente</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tipoVinculo"
                      checked={modalCriarNovo}
                      onChange={() => setModalCriarNovo(true)}
                      className="text-slate-900 focus:ring-slate-900"
                    />
                    <span>Criar novo cliente na Pieltec</span>
                  </label>
                </div>

                {!modalCriarNovo ? (
                  <div className="space-y-1">
                    <label htmlFor="omie-cliente-local" className="text-sm font-medium">Cliente local *</label><LookupCombobox id="omie-cliente-local" endpoint="/api/v1/omie/lookups/clientes" label="Cliente local" value={modalClienteId} onChange={setModalClienteId} required />
                  </div>
                ) : (
                  <div className="p-3 bg-blue-50/60 border border-blue-200 text-blue-900 rounded-lg text-xs">
                    Um novo registro será criado em <strong>Cliente</strong> com razão social e código sugerido a partir da Omie.
                  </div>
                )}
              </div>

              {/* Justificativa Obrigatória */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-700" htmlFor="field-omie-clientes-page-36114">
                  Motivo da conciliação *
                </label>
                <textarea id="field-omie-clientes-page-36114"
                  rows={2}
                  placeholder="Ex: Razão social conferida com contrato e aprovada pelo PCP."
                  value={modalJustificativa}
                  onChange={(e) => setModalJustificativa(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-xs bg-white text-slate-900 placeholder:text-slate-400 focus:ring-1 focus:ring-slate-900"
                  required
                />
              </div>

              {/* Checkbox de Confirmação Explícita */}
              <div className="p-3 bg-slate-50 border rounded-lg flex items-start gap-2.5">
                <input
                  type="checkbox"
                  id="modal-confirmacao"
                  checked={modalConfirmado}
                  onChange={(e) => setModalConfirmado(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                />
                <label htmlFor="modal-confirmacao" className="text-xs text-slate-700 cursor-pointer select-none">
                  <span className="font-bold block">Confirmo o vínculo permanente e irreversível (D-01)</span>
                  <span className="text-slate-500 block text-[11px]">
                    Não será permitida reatribuição a outro cliente sem migração administrativa explícita.
                  </span>
                </label>
              </div>

              {modalErro && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{modalErro}</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setItemParaVincular(null)}
                  className="px-3 py-2 border rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={modalLoading || !modalConfirmado || !modalJustificativa.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {modalLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Gravando Vínculo...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      <span>Confirmar Vínculo Permanente</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}
    </div>
  );
};
